import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { AlertDialogDescription } from '@/components/ui/alert-dialog';
import { execute } from '@/graphql/execute';
import { useAlert } from '@/providers/AlertProvider';
import { deleteBatchMutation } from '@/queries/attachment.query';

import { ANALYSIS_RUN_FILES_KEY } from './runFilesKey';
import { useCanRunAnalyses } from './useCanRunAnalyses';
import { analysisRunsQueryKey } from './useAnalysisRuns';

/** Shared by the run sidebar and context menu: same permissions,
 * confirmation, batch deletion and cache invalidation. A paired comparison
 * (one hub row, two batches) is deleted as a whole: every batch of the row,
 * behind one confirmation that says so.
 *
 * A run still queued or running is NOT blocked (ruling R26): with no
 * stuck-job sweeper, a stuck run must stay deletable from its row. Its
 * confirmation adds a warning line instead. Bulk deletion keeps its own
 * ACTIVE_JOB rule. */
export function useDeleteAnalysisRun(projectId: string, onDeleted?: (batchIds: string[]) => void) {
  const { t } = useTranslation('project');
  const queryClient = useQueryClient();
  const { openAlert } = useAlert();
  const canDelete = useCanRunAnalyses();
  const deleteRun = useMutation({
    // Every batch is attempted: one that fails does not keep the others.
    mutationFn: async (batchIds: string[]) => {
      const results = await Promise.allSettled(batchIds.map((id) => execute(deleteBatchMutation, { id })));
      const failed = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failed) throw failed.reason;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey(projectId) });
      void queryClient.invalidateQueries({ queryKey: [ANALYSIS_RUN_FILES_KEY] });
      // Deleting the batch also removes its result layers.
      void queryClient.invalidateQueries({ queryKey: ['layers', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['batches'] });
    },
    onSuccess: (_, batchIds) => {
      toast.success(t('analyses.details.deleted'));
      onDeleted?.(batchIds);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error && error.message ? error.message : t('analyses.details.deleteFailed'));
    },
  });
  const confirmDelete = (batchIds: string | readonly string[], options: { active?: boolean } = {}) => {
    const ids = typeof batchIds === 'string' ? [batchIds] : [...batchIds];
    if (!canDelete || deleteRun.isPending || ids.length === 0) return;
    const comparison = ids.length > 1;
    const description = t(
      comparison ? 'analyses.details.deleteComparisonDescription' : 'analyses.details.deleteDescription',
    );
    openAlert({
      title: t(comparison ? 'analyses.details.deleteComparisonTitle' : 'analyses.details.deleteTitle'),
      content: options.active ? (
        <AlertDialogDescription asChild>
          <div className='flex flex-col gap-2'>
            <span>{description}</span>
            <span
              data-testid='analysis-run-delete-active-warning'
              className='font-medium text-amber-700 dark:text-amber-400'>
              {t('analyses.details.deleteActiveWarning')}
            </span>
          </div>
        </AlertDialogDescription>
      ) : (
        description
      ),
      actions: [{ label: t('batch.delete'), variant: 'destructive', onClick: () => deleteRun.mutate(ids) }],
    });
  };
  return { canDelete, isDeleting: deleteRun.isPending, confirmDelete };
}
